import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  Section,
  SectionBody,
  Container,
  ToggleContainer,
  ToggleLabel,
} from './StyledComponents';

import ToggleSwitch from '../../../../shared/FormStyles/ToggleSwitch.jsx';

import Text from '../../../../shared/Text';
import { ReminderDateInput } from '../Posts/ReminderDateInput.jsx';
import Button from '../../../../shared/Button';
import { MessageTextInput } from '../Posts/MessageTextInput.jsx';
import Select from '../../../../shared/Select/Select.jsx';
import { useCampaignContext } from '../../contexts/CampaignContext.jsx';

export const ReminderForm = ({
  message,
  values,
  campaignId,
  publishDate,
  maxReminderDate,
  setEnableReminder,
  enableReminder,
  setFieldValue,
  onSendTest,
  handleChange,
}) => {
  const { t } = useTranslation('posts');
  const { campaign } = useCampaignContext();
  const isReadOnly = message.reminderSent || !!campaign?.deletedAt;
  const reminderTemplateOptions = [
    { value: 'with_tiles', label: 'Tiles with Images' },
    { value: 'only_text', label: 'Plain text with CTA' },
  ];

  return (
    <Container>
      <Section>
        <Text padding="14px 24px" as="div" variant="h6">
          Set Reminders
        </Text>
        <SectionBody>
          <ToggleContainer>
            <ToggleSwitch
              id="enableReminder"
              name="enableReminder"
              onChange={() => {
                setEnableReminder(prevState => !prevState);
                setFieldValue('reminderDate', null);
                setFieldValue('reminderSendHour', null);
              }}
              value={enableReminder}
              disabled={isReadOnly}
              label="Send Reminders"
              invertLabelPosition
            />
            <ToggleLabel
              isReminderTab={false}
              data-tip={'Enable Post Reminders'}
            >
              <i className="fa-regular fa-circle-info"></i>
            </ToggleLabel>
          </ToggleContainer>
          {enableReminder && (
            <>
              <ReminderDateInput
                reminderDate={values.reminderDate}
                changeReminderDate={({
                  publishDate: reminderDate,
                  sendHour,
                }) => {
                  setFieldValue('reminderDate', reminderDate);
                  setFieldValue('reminderSendHour', sendHour);
                }}
                reminderSendHour={values.reminderSendHour}
                campaignId={campaignId}
                postDate={publishDate}
                maxReminderDate={maxReminderDate}
                reminderSent={isReadOnly}
              />
              <LabelContainer>
                <SelectLabel htmlFor="reminderTemplate">
                  {t('reviewModal.messageForm.emailTemplate')}
                </SelectLabel>
                <Select
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isSearchable={false}
                  onChange={option =>
                    setFieldValue('reminderTemplate', option.value)
                  }
                  options={reminderTemplateOptions}
                  value={reminderTemplateOptions.find(
                    item => item.value === values.reminderTemplate,
                  )}
                  name="reminderTemplate"
                  isDisabled={isReadOnly}
                  id="reminderTemplate"
                />
              </LabelContainer>
              <LabelContainer>
                <MessageTextInput
                  onChange={handleChange}
                  value={values.reminderSubject}
                  placeholder={'Reminder Subject'}
                  name="reminderSubject"
                  isReminderTab={isReadOnly}
                  charactersLimit={100}
                  label={'Reminder Subject'}
                  labelVariant="subtitle2"
                  color="black.60"
                />
              </LabelContainer>
              {values.reminderTemplate === 'only_text' && (
                <>
                  <LabelContainer>
                    <MessageTextInput
                      onChange={handleChange}
                      value={values.reminderBody}
                      placeholder={'Message Body'}
                      name="reminderBody"
                      as="textarea"
                      label={'Reminder Message'}
                      labelVariant="subtitle2"
                      color="black.60"
                      isReminderTab={isReadOnly}
                    />
                  </LabelContainer>
                  <LabelContainer>
                    <MessageTextInput
                      onChange={handleChange}
                      value={values.reminderCta}
                      placeholder={t('reviewModal.messageForm.defaultCta')}
                      name="reminderCta"
                      charactersLimit={80}
                      label={'Button Text'}
                      labelVariant="subtitle2"
                      color="black.60"
                      isReminderTab={isReadOnly}
                    />
                  </LabelContainer>
                  <LabelContainer>
                    <MessageTextInput
                      onChange={handleChange}
                      value={values.reminderFooter}
                      placeholder={'Footer Message'}
                      name="reminderFooter"
                      as="textarea"
                      label={'Footer Message'}
                      labelVariant="subtitle2"
                      color="black.60"
                      isReminderTab={isReadOnly}
                    />
                  </LabelContainer>
                </>
              )}

              {!isReadOnly && (
                <Button
                  onClick={() => onSendTest()}
                  mode="outline"
                  variant="black"
                  width="100%"
                  height="40px"
                  mt="12px"
                >
                  Send Test Message To Myself
                </Button>
              )}
            </>
          )}
        </SectionBody>
      </Section>
    </Container>
  );
};

const LabelContainer = styled.div`
  margin-bottom: 24px;

  &:nth-child(2) {
    margin-bottom: 12px;
  }
`;

// Plain attrs rather than defaultAttrs: Text's own color default is applied
// first, and SelectLabel's color has to win over it (nobody passes these).
const SelectLabel = styled(Text).attrs({
  variant: 'subtitle2',
  color: 'black.60',
  as: 'label',
})`
  margin-bottom: 8px;
  text-align: left;
  cursor: ${props => (props.link ? 'pointer' : 'auto')};
`;
